import { Button, Card } from '@repo/shadcn';
import { FC } from 'react';
import { ZodSchema } from 'zod';

import { IwForm } from '@components/form';
import { SelectField, TextField } from '@components/form/fields';
import { SectionContainer } from '@components/shared';
import { HeaderBreadcrumb, SectionTitle } from '@components/ui';

import { Route } from '@/constants';

import { useCreateUser } from './create-user.hook';
import {
    CreateUserFormSchema,
    INITIAL_VALUES,
    TCreateUserForm,
} from './create-user.schema';

type CreateUserProps = {
    initialValues?: Partial<TCreateUserForm>;
    handleSubmit?: (values: TCreateUserForm) => Promise<void>;
    submitButtonText?: string;
    schema?: ZodSchema;
    breadcrumbLabel?: string;
    isSubmitting?: boolean;
    title?: string;
};

export const CreateUser: FC<CreateUserProps> = ({
    initialValues = INITIAL_VALUES,
    handleSubmit,
    schema,
    submitButtonText = 'Create User',
    breadcrumbLabel = 'Create New User',
    isSubmitting = false,
    title = 'Create New User',
}) => {
    const {
        handleSubmit: handleCreateUser,
        roleOptions,
        isRolesLoading,
        isUserCreating,
    } = useCreateUser();

    const isSubmittingFinal = isSubmitting || isUserCreating;

    return (
        <SectionContainer>
            <IwForm
                schema={schema || CreateUserFormSchema}
                initialValues={initialValues}
                onSubmit={(data) => {
                    if (handleSubmit) {
                        handleSubmit(data as TCreateUserForm);
                    } else {
                        handleCreateUser(data as TCreateUserForm);
                    }
                }}
            >
                <Card className={'space-y-4 p-4'}>
                    <HeaderBreadcrumb
                        items={[
                            { label: 'Users', href: Route.USERS },
                            { label: breadcrumbLabel },
                        ]}
                    />
                    <SectionTitle>{title}</SectionTitle>

                    <div className={'grid grid-cols-1 gap-4 lg:grid-cols-2'}>
                        <TextField<TCreateUserForm>
                            name={'name'}
                            label={'Name'}
                            placeholder={'Name'}
                        />

                        <TextField<TCreateUserForm>
                            name={'email'}
                            label={'Email'}
                            type={'email'}
                            placeholder={'example@example'}
                        />
                        <TextField<TCreateUserForm>
                            name={'phone_number'}
                            label={'Phone Number'}
                            placeholder={'0123456789'}
                        />
                        <TextField<TCreateUserForm>
                            name={'password'}
                            label={'Password'}
                            type={'password'}
                            placeholder={'password'}
                            inputProps={{
                                autoComplete: 'off',
                            }}
                        />

                        <SelectField<TCreateUserForm>
                            name={'status'}
                            label={'Status'}
                            options={[
                                { label: 'Active', value: 'active' },
                                { label: 'Suspended', value: 'suspended' },
                            ]}
                        />

                        {isRolesLoading ? (
                            <div>Loading roles...</div>
                        ) : (
                            <SelectField<TCreateUserForm>
                                name={'role_id'}
                                label={'Role'}
                                options={roleOptions}
                            />
                        )}
                    </div>

                    <div>
                        <Button type={'submit'} disabled={isSubmittingFinal}>
                            {isSubmittingFinal
                                ? 'Submitting...'
                                : submitButtonText}
                        </Button>
                    </div>
                </Card>
            </IwForm>
        </SectionContainer>
    );
};

CreateUser.displayName = 'CreateUser';
